import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Ticket, Search, Check, X, QrCode, Loader2, Shield, 
  User, Calendar, CreditCard, AlertCircle, CheckCircle2,
  Camera, CameraOff, RefreshCw, Clock, Hash, Users,
  Fingerprint, ScanLine, Eye, ChevronDown
} from 'lucide-react';
import { Html5Qrcode } from 'html5-qrcode';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';
import { SidebarProvider } from '../../components/AdminSidebarNew';
import { useAuth } from '../../context/AuthContext';
import api, { ticketsApi, eventsApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const TicketsAdminPage = () => {
  const { hasPermission } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [filterEvent, setFilterEvent] = useState('all');
  const [scanMode, setScanMode] = useState('dni'); // 'dni' or 'qr'
  const [lastResult, setLastResult] = useState(null);
  const [scanHistory, setScanHistory] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [cameraPermission, setCameraPermission] = useState('prompt');
  const [showTicketList, setShowTicketList] = useState(true); // Show by default
  const [stats, setStats] = useState({ total: 0, checkedIn: 0, pending: 0 });
  const [ticketSearch, setTicketSearch] = useState(''); // Search filter for ticket list
  
  const html5QrcodeRef = useRef(null);
  const lastScanRef = useRef({ code: null, time: 0 });
  const inputRef = useRef(null);

  const canView = hasPermission('tickets.view');
  const canValidate = hasPermission('tickets.validate');

  useEffect(() => {
    if (canView) {
      fetchEvents();
      checkCameraPermission();
    }
    return () => { stopScanner(); };
  }, [canView]);

  useEffect(() => {
    if (filterEvent !== 'all') {
      fetchTickets();
      calculateStats();
    }
  }, [filterEvent]);

  const checkCameraPermission = async () => {
    try {
      const result = await navigator.permissions.query({ name: 'camera' });
      setCameraPermission(result.state);
      result.addEventListener('change', () => setCameraPermission(result.state));
    } catch (error) {
      setCameraPermission('prompt');
    }
  };

  const fetchEvents = async () => {
    try {
      const eventsRes = await eventsApi.getAll();
      const activeEvents = (eventsRes.data || []).filter(e => 
        ['published', 'completed'].includes(e.status)
      );
      setEvents(activeEvents);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Error al cargar eventos');
    } finally {
      setLoading(false);
    }
  };

  const fetchTickets = async () => {
    if (filterEvent === 'all') return;
    try {
      console.log('Fetching tickets for event:', filterEvent);
      // Use the specific endpoint for event tickets
      const ticketsRes = await api.get(`/tickets/event/${filterEvent}`);
      console.log('Tickets response:', ticketsRes.data);
      setTickets(ticketsRes.data || []);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      setTickets([]);
    }
  };

  const calculateStats = () => {
    const filtered = tickets.filter(t => filterEvent === 'all' || t.event_id === filterEvent);
    setStats({
      total: filtered.length,
      checkedIn: filtered.filter(t => t.status === 'used').length,
      // Count "valid", "confirmed", and "active" as pending
      pending: filtered.filter(t => ['valid', 'confirmed', 'active'].includes(t.status)).length
    });
  };

  useEffect(() => {
    calculateStats();
  }, [tickets, filterEvent]);

  // Camera Scanner Functions
  const startScanner = async () => {
    if (filterEvent === 'all') {
      toast.error('Seleccioná un evento primero');
      return;
    }

    setCameraLoading(true);
    
    try {
      const readerElement = document.getElementById('qr-reader-admin');
      if (readerElement) {
        readerElement.style.display = 'block';
        readerElement.innerHTML = '';
      }
      
      const html5Qrcode = new Html5Qrcode("qr-reader-admin");
      html5QrcodeRef.current = html5Qrcode;
      
      const config = { 
        fps: 10, 
        qrbox: { width: 220, height: 220 },
        aspectRatio: 1.0,
        disableFlip: false
      };
      
      await html5Qrcode.start(
        { facingMode: "environment" },
        config,
        onScanSuccess,
        () => {}
      );
      
      setScanning(true);
      setCameraPermission('granted');
      setCameraLoading(false);
      toast.success('Cámara activada');
    } catch (error) {
      console.error('Camera error:', error);
      setCameraLoading(false);
      if (error.toString().includes('Permission') || error.toString().includes('denied')) {
        setCameraPermission('denied');
        toast.error('Permiso de cámara denegado');
      } else {
        toast.error('Error al iniciar cámara: ' + error.message);
      }
    }
  };

  const stopScanner = async () => {
    if (html5QrcodeRef.current) {
      try {
        await html5QrcodeRef.current.stop();
        html5QrcodeRef.current = null;
      } catch (error) {}
    }
    setScanning(false);
  };

  const onScanSuccess = async (decodedText) => {
    const now = Date.now();
    if (decodedText === lastScanRef.current.code && now - lastScanRef.current.time < 4000) return;
    if (now - lastScanRef.current.time < 1200) return;
    lastScanRef.current = { code: decodedText, time: now };
    if (navigator.vibrate) navigator.vibrate(100);
    try { html5QrcodeRef.current?.pause(true); } catch (e) {}
    await verifyCode(decodedText, 'qr');
    setTimeout(() => {
      try { html5QrcodeRef.current?.resume(); } catch (e) {}
    }, 1600);
  };

  // Verify Code (DNI or QR)
  const verifyCode = async (code, method = scanMode) => {
    if (filterEvent === 'all') {
      toast.error('Seleccioná un evento primero');
      return;
    }

    const codeClean = code.trim();
    if (!codeClean) return;

    setValidating(true);
    setLastResult(null);

    try {
      let response;
      
      if (method === 'dni') {
        response = await ticketsApi.validateByDni(codeClean, filterEvent);
      } else {
        // Check if it's a QR data format (BRAVO|...|...|...)
        const isQrData = codeClean.includes('|');
        if (isQrData) {
          response = await ticketsApi.validateByQr(codeClean);
        } else {
          // Pass event_id for manual code validation to update producer stats
          response = await ticketsApi.validate(codeClean.toUpperCase(), filterEvent);
        }
      }

      const resultData = {
        success: response.data.success !== false && !response.data.already_checked_in,
        method: method,
        code: codeClean,
        message: response.data.already_checked_in 
          ? 'ENTRADA YA VALIDADA' 
          : (response.data.success !== false ? 'ACCESO PERMITIDO' : (response.data.message || 'Error')),
        alreadyUsed: response.data.already_checked_in,
        data: response.data.ticket || response.data,
        timestamp: new Date()
      };

      setLastResult(resultData);
      setScanHistory(prev => [resultData, ...prev.slice(0, 19)]);
      fetchTickets();

      if (resultData.alreadyUsed) {
        toast.warning(`⚠️ Entrada ya validada - ${resultData.data?.buyer_name || ''}`);
      } else if (resultData.success) {
        toast.success(`✓ Entrada válida - ${resultData.data?.buyer_name || ''}`);
      } else {
        toast.error(resultData.message);
      }

    } catch (error) {
      const detail = error.response?.data?.detail || 'Código no encontrado';
      const resultData = {
        success: false,
        method: method,
        code: codeClean,
        message: detail,
        timestamp: new Date()
      };
      
      setLastResult(resultData);
      setScanHistory(prev => [resultData, ...prev.slice(0, 19)]);
      toast.error(detail);
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputValue.trim()) return;
    await verifyCode(inputValue.trim(), scanMode);
    setInputValue('');
    if (inputRef.current) inputRef.current.focus();
  };

  const resetAndContinue = () => {
    setLastResult(null);
    if (scanMode === 'qr') {
      if (!scanning) startScanner();
    } else if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Auto-focus input
  useEffect(() => {
    if (scanMode === 'dni' && inputRef.current && !lastResult) {
      inputRef.current.focus();
    }
  }, [scanMode, lastResult, filterEvent]);

  if (!canView) {
    return (
      <SidebarProvider>
        <AdminLayout>
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <Shield size={48} className="mx-auto mb-4 text-zinc-600" />
              <h3 className="text-xl font-black uppercase mb-2">Sin Acceso</h3>
              <p className="text-zinc-500">No tienes permiso para ver tickets.</p>
            </div>
          </div>
        </AdminLayout>
      </SidebarProvider>
    );
  }

  const selectedEventData = events.find(e => e.id === filterEvent);

  return (
    <SidebarProvider>
      <AdminLayout>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="p-4 lg:p-8"
          data-testid="tickets-admin"
        >
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#D4AF37]/20 rounded-2xl mb-4">
                <Fingerprint size={32} className="text-[#D4AF37]" />
              </div>
              <h1 className="text-2xl lg:text-3xl font-black text-white uppercase tracking-tight">
                Control de Entradas
              </h1>
              <p className="text-zinc-500 mt-2">Validación por DNI o escaneo QR</p>
            </div>

            {/* Event Selector - Prominent */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 mb-6">
              <label className="text-sm text-zinc-400 block mb-3 font-medium flex items-center gap-2">
                <Calendar size={16} className="text-[#D4AF37]" />
                Seleccionar Evento
              </label>
              <Select value={filterEvent} onValueChange={setFilterEvent}>
                <SelectTrigger className="w-full bg-zinc-900 border-2 border-zinc-700 rounded-xl h-14 text-lg hover:border-[#D4AF37]/50 transition-colors" data-testid="event-selector">
                  <SelectValue placeholder="Seleccioná un evento para comenzar" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border border-zinc-700">
                  <SelectItem value="all">-- Seleccionar evento --</SelectItem>
                  {events.map(event => (
                    <SelectItem key={event.id} value={event.id}>
                      <div className="flex items-center gap-2">
                        <Ticket size={14} className="text-[#D4AF37]" />
                        {event.title}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Stats */}
              {filterEvent !== 'all' && (
                <div className="grid grid-cols-3 gap-4 mt-6">
                  <div className="bg-zinc-800/50 rounded-xl p-4 text-center">
                    <p className="text-2xl lg:text-3xl font-black text-white">{stats.total}</p>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1">Total</p>
                  </div>
                  <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4 text-center">
                    <p className="text-2xl lg:text-3xl font-black text-green-400">{stats.checkedIn}</p>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1">Ingresados</p>
                  </div>
                  <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4 text-center">
                    <p className="text-2xl lg:text-3xl font-black text-yellow-400">{stats.pending}</p>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1">Pendientes</p>
                  </div>
                </div>
              )}
              
              {/* Debug info when no tickets */}
              {filterEvent !== 'all' && stats.total === 0 && (
                <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl">
                  <p className="text-amber-400 text-sm">
                    <AlertCircle size={14} className="inline mr-2" />
                    No se encontraron entradas para este evento.
                  </p>
                  <p className="text-amber-400/70 text-xs mt-1">
                    Event ID: <code className="bg-black/30 px-1 rounded">{filterEvent}</code>
                  </p>
                </div>
              )}
            </div>

            {filterEvent !== 'all' && canValidate && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column - Scanner */}
                <div className="space-y-6">
                  {/* Mode Selector */}
                  <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-2 grid grid-cols-2 gap-2">
                    <button
                      onClick={() => { stopScanner(); setScanMode('dni'); }}
                      className={`flex flex-col items-center justify-center gap-2 py-4 px-4 rounded-xl font-bold transition-all ${
                        scanMode === 'dni'
                          ? 'bg-[#D4AF37] text-black'
                          : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                      }`}
                      data-testid="mode-dni"
                    >
                      <CreditCard size={24} />
                      <span className="text-sm">Validar por DNI</span>
                      <span className="text-[10px] opacity-70">Método principal</span>
                    </button>
                    <button
                      onClick={() => { stopScanner(); setScanMode('qr'); }}
                      className={`flex flex-col items-center justify-center gap-2 py-4 px-4 rounded-xl font-bold transition-all ${
                        scanMode === 'qr'
                          ? 'bg-[#D4AF37] text-black'
                          : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                      }`}
                      data-testid="mode-qr"
                    >
                      <QrCode size={24} />
                      <span className="text-sm">Escanear QR</span>
                      <span className="text-[10px] opacity-70">Alternativo</span>
                    </button>
                  </div>

                  {/* DNI Input Mode */}
                  {scanMode === 'dni' && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6"
                    >
                      <div className="text-center mb-6">
                        <div className="w-20 h-20 bg-[#D4AF37]/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                          <CreditCard size={40} className="text-[#D4AF37]" />
                        </div>
                        <h3 className="text-lg font-bold text-white">Ingresá el DNI</h3>
                        <p className="text-zinc-500 text-sm mt-1">
                          El método principal de validación es el DNI del titular
                        </p>
                      </div>
                      
                      <form onSubmit={handleSubmit}>
                        <Input
                          ref={inputRef}
                          value={inputValue}
                          onChange={(e) => setInputValue(e.target.value.replace(/[^0-9.]/g, ''))}
                          placeholder="Ej: 12345678"
                          className="bg-zinc-800 border-2 border-zinc-700 focus:border-[#D4AF37] text-center text-2xl font-mono h-16 mb-4 rounded-xl"
                          disabled={validating}
                          maxLength={10}
                          autoFocus
                          data-testid="dni-input"
                        />
                        <Button
                          type="submit"
                          disabled={validating || !inputValue.trim()}
                          className="w-full bg-green-500 hover:bg-green-600 text-black font-bold h-14 text-lg rounded-xl"
                          data-testid="validate-btn"
                        >
                          {validating ? (
                            <Loader2 className="animate-spin mr-2" size={24} />
                          ) : (
                            <Check size={24} className="mr-2" />
                          )}
                          VALIDAR DNI
                        </Button>
                      </form>
                      
                      <div className="mt-4 p-3 bg-zinc-800/50 rounded-xl">
                        <p className="text-xs text-zinc-400 text-center flex items-center justify-center gap-2">
                          <Shield size={12} />
                          Una vez validado por DNI, el código QR quedará inhabilitado
                        </p>
                      </div>
                    </motion.div>
                  )}

                  {/* QR Scanner Mode */}
                  {scanMode === 'qr' && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="space-y-4"
                    >
                      {/* Camera Scanner */}
                      <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl overflow-hidden">
                        <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
                          <h3 className="font-bold text-white flex items-center gap-2">
                            <Camera size={18} className="text-[#D4AF37]" />
                            Escáner QR
                          </h3>
                          {cameraPermission === 'denied' && (
                            <span className="text-xs text-red-400 flex items-center gap-1">
                              <CameraOff size={12} /> Sin permiso
                            </span>
                          )}
                        </div>
                        
                        <div 
                          id="qr-reader-admin" 
                          style={{ 
                            width: '100%', 
                            minHeight: scanning ? '350px' : '0px',
                            maxHeight: '450px',
                            display: scanning ? 'block' : 'none',
                            backgroundColor: '#000',
                            position: 'relative',
                            overflow: 'hidden'
                          }}
                        />
                        
                        {!scanning && !cameraLoading && (
                          <div className="aspect-square max-h-[300px] flex flex-col items-center justify-center p-8 bg-zinc-950">
                            <div className="relative mb-6">
                              <div className="w-32 h-32 border-2 border-dashed border-zinc-700 rounded-2xl flex items-center justify-center">
                                <QrCode size={48} className="text-zinc-700" />
                              </div>
                              <div className="absolute -top-1 -left-1 w-6 h-6 border-t-2 border-l-2 border-[#D4AF37] rounded-tl-lg" />
                              <div className="absolute -top-1 -right-1 w-6 h-6 border-t-2 border-r-2 border-[#D4AF37] rounded-tr-lg" />
                              <div className="absolute -bottom-1 -left-1 w-6 h-6 border-b-2 border-l-2 border-[#D4AF37] rounded-bl-lg" />
                              <div className="absolute -bottom-1 -right-1 w-6 h-6 border-b-2 border-r-2 border-[#D4AF37] rounded-br-lg" />
                            </div>
                            <Button
                              onClick={startScanner}
                              className="bg-[#D4AF37] hover:bg-[#B8962E] text-black font-bold px-8 rounded-xl"
                              data-testid="start-camera-btn"
                            >
                              <Camera size={18} className="mr-2" />
                              Iniciar Cámara
                            </Button>
                          </div>
                        )}
                        
                        {cameraLoading && (
                          <div className="aspect-square max-h-[300px] flex flex-col items-center justify-center p-8 bg-zinc-950">
                            <Loader2 size={48} className="text-[#D4AF37] animate-spin mb-4" />
                            <p className="text-zinc-400 text-sm">Iniciando cámara...</p>
                          </div>
                        )}
                        
                        {scanning && (
                          <div className="p-4 border-t border-zinc-800">
                            <Button
                              onClick={stopScanner}
                              variant="outline"
                              className="w-full border-red-500/50 text-red-400 hover:bg-red-500/10 rounded-xl"
                            >
                              <CameraOff size={16} className="mr-2" />
                              Detener Cámara
                            </Button>
                          </div>
                        )}
                      </div>

                      {/* Manual Code Input */}
                      <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-4">
                        <h4 className="font-bold text-white mb-3 flex items-center gap-2">
                          <Hash size={16} className="text-zinc-500" />
                          O ingresá el código manualmente
                        </h4>
                        <form onSubmit={handleSubmit} className="flex gap-2">
                          <div className="flex-1 flex items-stretch bg-zinc-800 border-2 border-[#D4AF37] rounded-xl overflow-hidden">
                            <div className="w-14 flex items-center justify-center bg-[#D4AF37] text-black font-mono font-bold text-sm flex-shrink-0">
                              BP-
                            </div>
                            <input
                              value={inputValue}
                              onChange={(e) => setInputValue(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                              placeholder="0B799478"
                              className="flex-1 min-w-0 bg-zinc-900 px-3 py-2 font-mono text-base text-white outline-none placeholder:text-zinc-600 uppercase"
                              disabled={validating}
                              data-testid="code-input"
                            />
                          </div>
                          <Button
                            type="submit"
                            disabled={validating || !inputValue.trim()}
                            className="bg-[#D4AF37] hover:bg-[#B8962E] text-black px-6 rounded-xl"
                          >
                            {validating ? <Loader2 className="animate-spin" size={18} /> : <Search size={18} />}
                          </Button>
                        </form>
                      </div>
                    </motion.div>
                  )}
                </div>

                {/* Right Column - Results */}
                <div className="space-y-6">
                  {/* Last Result */}
                  <AnimatePresence mode="wait">
                    {lastResult ? (
                      <motion.div
                        key="result"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        className={`rounded-2xl overflow-hidden ${
                          lastResult.success 
                            ? 'bg-gradient-to-br from-green-500/20 to-green-500/5 border-2 border-green-500/30' 
                            : lastResult.alreadyUsed
                            ? 'bg-gradient-to-br from-yellow-500/20 to-yellow-500/5 border-2 border-yellow-500/30'
                            : 'bg-gradient-to-br from-red-500/20 to-red-500/5 border-2 border-red-500/30'
                        }`}
                      >
                        <div className={`p-8 text-center ${
                          lastResult.success 
                            ? 'bg-green-500/10' 
                            : lastResult.alreadyUsed 
                            ? 'bg-yellow-500/10'
                            : 'bg-red-500/10'
                        }`}>
                          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", duration: 0.5 }}>
                            {lastResult.success ? (
                              <CheckCircle2 size={80} className="text-green-400 mx-auto mb-4" />
                            ) : lastResult.alreadyUsed ? (
                              <AlertCircle size={80} className="text-yellow-400 mx-auto mb-4" />
                            ) : (
                              <X size={80} className="text-red-400 mx-auto mb-4" />
                            )}
                          </motion.div>
                          <h3 className={`text-2xl font-black ${
                            lastResult.success 
                              ? 'text-green-400' 
                              : lastResult.alreadyUsed 
                              ? 'text-yellow-400'
                              : 'text-red-400'
                          }`}>
                            {lastResult.success ? '¡ACCESO PERMITIDO!' : lastResult.alreadyUsed ? 'YA INGRESÓ' : 'ACCESO DENEGADO'}
                          </h3>
                          <p className="text-zinc-400 mt-1">{lastResult.message}</p>
                        </div>
                        
                        <div className="p-6 space-y-4">
                          {/* Method Badge */}
                          <div className="flex items-center justify-center gap-2">
                            <span className={`px-4 py-2 rounded-full text-sm font-bold ${
                              lastResult.method === 'dni'
                                ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                                : 'bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/30'
                            }`}>
                              {lastResult.method === 'dni' ? (
                                <><CreditCard size={14} className="inline mr-2" /> Validado por DNI</>
                              ) : (
                                <><QrCode size={14} className="inline mr-2" /> Validado por QR</>
                              )}
                            </span>
                          </div>

                          {/* Attendee Info */}
                          {lastResult.data && (
                            <div className="bg-zinc-900/50 rounded-xl p-4 space-y-3">
                              {lastResult.data.buyer_name && (
                                <div className="flex items-center gap-3">
                                  <User size={18} className="text-zinc-500" />
                                  <span className="text-white font-semibold text-lg">
                                    {lastResult.data.buyer_name}
                                  </span>
                                </div>
                              )}
                              {(lastResult.data.holder_dni || lastResult.data.buyer_dni) && (
                                <div className="flex items-center gap-3">
                                  <CreditCard size={16} className="text-zinc-500" />
                                  <span className="text-zinc-400">DNI: {lastResult.data.holder_dni || lastResult.data.buyer_dni}</span>
                                </div>
                              )}
                              {lastResult.data.tier_name && (
                                <div className="flex items-center gap-3">
                                  <Ticket size={16} className="text-zinc-500" />
                                  <span className="text-zinc-400">{lastResult.data.tier_name}</span>
                                </div>
                              )}
                              {lastResult.data.event_title && (
                                <div className="flex items-center gap-3">
                                  <Calendar size={16} className="text-zinc-500" />
                                  <span className="text-zinc-400 text-sm">{lastResult.data.event_title}</span>
                                </div>
                              )}
                            </div>
                          )}

                          <Button 
                            onClick={resetAndContinue} 
                            className="w-full bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl h-12"
                            data-testid="next-btn"
                          >
                            <RefreshCw size={16} className="mr-2" />
                            Siguiente Entrada
                          </Button>
                        </div>
                      </motion.div>
                    ) : (
                      <motion.div
                        key="waiting"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-8 text-center"
                      >
                        <div className="w-20 h-20 bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-4">
                          <ScanLine size={36} className="text-zinc-600" />
                        </div>
                        <h3 className="text-lg font-bold text-white mb-2">Esperando validación</h3>
                        <p className="text-zinc-500 text-sm">
                          {scanMode === 'dni' 
                            ? 'Ingresá el DNI del asistente' 
                            : 'Escaneá el código QR o ingresalo manualmente'}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Security Notice */}
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-2xl p-4">
                    <div className="flex items-start gap-3">
                      <Shield className="text-blue-400 flex-shrink-0 mt-0.5" size={20} />
                      <div>
                        <p className="text-blue-400 font-medium text-sm">Sistema anti-duplicación</p>
                        <p className="text-blue-400/70 text-xs mt-1">
                          Cada entrada puede validarse solo una vez. Si se usa el DNI, el QR queda inhabilitado y viceversa.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Scan History */}
                  {scanHistory.length > 0 && (
                    <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl overflow-hidden">
                      <div className="p-4 border-b border-zinc-800">
                        <h3 className="font-bold text-white flex items-center gap-2">
                          <Clock size={16} className="text-zinc-500" />
                          Últimas validaciones
                        </h3>
                      </div>
                      <div className="max-h-48 overflow-y-auto">
                        {scanHistory.slice(0, 10).map((scan, i) => (
                          <div 
                            key={i}
                            className={`p-3 border-b border-zinc-800/50 last:border-0 flex items-center gap-3 ${
                              scan.success ? 'bg-green-500/5' : 'bg-red-500/5'
                            }`}
                          >
                            {scan.success ? (
                              <CheckCircle2 size={16} className="text-green-400 flex-shrink-0" />
                            ) : (
                              <X size={16} className="text-red-400 flex-shrink-0" />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-white truncate">
                                {scan.data?.buyer_name || scan.code}
                              </p>
                              <p className="text-xs text-zinc-500">
                                {scan.method === 'dni' ? 'DNI' : 'QR'} • {scan.timestamp.toLocaleTimeString()}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Ticket List - Shows automatically when event is selected */}
            {filterEvent !== 'all' && (
              <div className="mt-8">
                {/* Header with toggle and search */}
                <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-4 mb-4">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <button
                      onClick={() => setShowTicketList(!showTicketList)}
                      className="flex items-center gap-3 text-white font-medium hover:text-[#D4AF37] transition-colors"
                      data-testid="toggle-list-btn"
                    >
                      <Users size={18} className="text-[#D4AF37]" />
                      <span>Base de datos de entradas ({tickets.length})</span>
                      <ChevronDown size={18} className={`text-zinc-500 transition-transform ${showTicketList ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {showTicketList && tickets.length > 0 && (
                      <div className="relative">
                        <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-500" />
                        <Input
                          value={ticketSearch}
                          onChange={(e) => setTicketSearch(e.target.value)}
                          placeholder="Buscar por DNI, nombre o código..."
                          className="pl-10 bg-zinc-800 border-zinc-700 w-full lg:w-80"
                          data-testid="ticket-search"
                        />
                      </div>
                    )}
                  </div>
                </div>

                <AnimatePresence>
                  {showTicketList && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl overflow-hidden">
                        {tickets.length === 0 ? (
                          <div className="text-center py-12">
                            <Ticket size={48} className="mx-auto mb-4 text-zinc-600" />
                            <p className="text-zinc-500">No hay entradas vendidas para este evento.</p>
                          </div>
                        ) : (
                          <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                            <table className="w-full">
                              <thead className="border-b border-zinc-800 bg-zinc-900 sticky top-0 z-10">
                                <tr>
                                  <th className="text-left text-xs uppercase tracking-widest text-zinc-500 p-4">DNI</th>
                                  <th className="text-left text-xs uppercase tracking-widest text-zinc-500 p-4">Código</th>
                                  <th className="text-left text-xs uppercase tracking-widest text-zinc-500 p-4">Nombre</th>
                                  <th className="text-left text-xs uppercase tracking-widest text-zinc-500 p-4">Email</th>
                                  <th className="text-left text-xs uppercase tracking-widest text-zinc-500 p-4">Tanda</th>
                                  <th className="text-left text-xs uppercase tracking-widest text-zinc-500 p-4">Estado</th>
                                </tr>
                              </thead>
                              <tbody>
                                {tickets
                                  .filter(ticket => {
                                    if (!ticketSearch.trim()) return true;
                                    const search = ticketSearch.toLowerCase();
                                    return (
                                      (ticket.holder_dni || ticket.buyer_dni || '').toLowerCase().includes(search) ||
                                      (ticket.buyer_name || '').toLowerCase().includes(search) ||
                                      (ticket.buyer_email || '').toLowerCase().includes(search) ||
                                      (ticket.ticket_code || '').toLowerCase().includes(search)
                                    );
                                  })
                                  .map((ticket) => (
                                  <tr key={ticket.id} className="border-b border-zinc-800/50 last:border-0 hover:bg-zinc-800/30">
                                    <td className="p-4">
                                      <span className="font-mono font-bold text-[#D4AF37]">
                                        {ticket.holder_dni || ticket.buyer_dni || '-'}
                                      </span>
                                    </td>
                                    <td className="p-4">
                                      <span className="font-mono text-sm text-zinc-400">{ticket.ticket_code}</span>
                                    </td>
                                    <td className="p-4">
                                      <p className="font-medium text-white">{ticket.buyer_name}</p>
                                    </td>
                                    <td className="p-4">
                                      <p className="text-sm text-zinc-500">{ticket.buyer_email}</p>
                                    </td>
                                    <td className="p-4">
                                      <span className="text-sm text-zinc-400">
                                        {ticket.tier_name || 'General'}
                                      </span>
                                    </td>
                                    <td className="p-4">
                                      <span className={`px-3 py-1 rounded-full text-xs uppercase font-bold ${
                                        ticket.status === 'used' 
                                          ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                                          : ['valid', 'confirmed', 'active'].includes(ticket.status)
                                          ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                                          : 'bg-zinc-500/20 text-zinc-400 border border-zinc-500/30'
                                      }`}>
                                        {ticket.status === 'used' ? 'Ingresó' : ['valid', 'confirmed', 'active'].includes(ticket.status) ? 'Pendiente' : ticket.status}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* No Event Selected Message */}
            {filterEvent === 'all' && (
              <div className="text-center py-16">
                <div className="w-24 h-24 bg-zinc-800/50 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Calendar size={48} className="text-zinc-600" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Seleccioná un evento</h3>
                <p className="text-zinc-500 max-w-md mx-auto">
                  Para comenzar a validar entradas, seleccioná un evento de la lista de arriba.
                </p>
              </div>
            )}
          </div>
        </motion.div>
      </AdminLayout>
    </SidebarProvider>
  );
};

export default TicketsAdminPage;
